/**
 * PROTECTED ROUTE COMPONENT
 *
 * Wrapper component for route protection and role-based authorization.
 * Handles authentication checks, active account verification, and role-based access control.
 * Admin roles inherit team access (e.g., IT_ADMIN has IT_TEAM access).
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import type { UserRole } from '@/types/database';
import { getTeamFromRole, getAdminTeam } from '@/lib/role-helpers';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole | UserRole[];
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading } = useAuth();

  // 1. Check if auth is loading - show loading spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // 2. Check if user exists - redirect to signin
  if (!user) {
    return <Navigate to="/auth/signin" replace />;
  }

  // 3. Check if user account is active - redirect to account-inactive
  if (!user.is_active) {
    return <Navigate to="/account-inactive" replace />;
  }

  // 4. Check role requirements (if specified)
  if (requiredRole) {
    // SYSTEM_ADMIN bypasses all role checks
    if (user.role === 'SYSTEM_ADMIN') {
      return <>{children}</>;
    }

    // Helper function to check if user role satisfies a required role
    const roleMatches = (userRole: UserRole, required: UserRole): boolean => {
      // Direct match
      if (userRole === required) {
        return true;
      }

      // Admin roles satisfy their team role requirements
      // e.g., IT_ADMIN satisfies IT_TEAM requirement
      const adminTeam = getAdminTeam(userRole);
      if (adminTeam) {
        // User is an admin, check if required role is their team's base role
        const requiredTeam = getTeamFromRole(required);
        return requiredTeam === adminTeam && required.endsWith('_TEAM');
      }

      return false;
    };

    // Check if user role matches required role(s)
    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.some(req => roleMatches(user.role, req))
      : roleMatches(user.role, requiredRole);

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // 5. All checks passed - render children
  return <>{children}</>;
}
